import { Backdrop, Box, Typography, CircularProgress } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * Full-screen blocking overlay used for long-running AI operations
 * (question generation, answer evaluation). Animated icon ring + bouncing
 * dots + a persistent "don't refresh" notice.
 *
 * Consolidates the formerly-duplicated GenerationOverlay (AddCategory) and
 * EvaluationOverlay (Test). Pass `note={null}` to hide the warning box.
 */
export const LoadingOverlay = ({
  open,
  title = "AI is working…",
  message = "",
  note = "Please don't refresh or close this window.",
  progress = null, // 0–100 → determinate ring + % in the center; null → indeterminate spinner
}) => {
  const hasProgress = Number.isFinite(progress);
  const pct = hasProgress ? Math.min(100, Math.max(0, Math.round(progress))) : 0;
  return (
  <Backdrop
    open={open}
    role="alertdialog"
    aria-busy={open}
    aria-live="assertive"
    aria-label={title}
    sx={{
      zIndex: (theme) => theme.zIndex.modal + 10,
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(15, 23, 42, 0.55)",
    }}
  >
    <Box
      sx={{
        width: "min(92vw, 440px)",
        backgroundColor: "#fff",
        borderRadius: "22px",
        p: 4,
        textAlign: "center",
        boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      {/* Animated icon ring */}
      <Box sx={{ position: "relative", width: 88, height: 88, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress
          size={88}
          thickness={3}
          variant={hasProgress ? "determinate" : "indeterminate"}
          value={hasProgress ? pct : undefined}
          sx={{ color: "#1c97e3", position: "absolute" }}
        />
        <Box
          sx={{
            width: 62,
            height: 62,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #1c97e3 0%, #03438A 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "tiqPulse 1.6s ease-in-out infinite",
            "@keyframes tiqPulse": {
              "0%, 100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(28,151,227,0.45)" },
              "50%": { transform: "scale(1.08)", boxShadow: "0 0 0 12px rgba(28,151,227,0)" },
            },
          }}
        >
          {hasProgress ? (
            <Typography sx={{ color: "#fff", fontSize: 17, fontWeight: 800, lineHeight: 1 }}>
              {pct}%
            </Typography>
          ) : (
            <AutoAwesomeIcon
              sx={{
                color: "#fff",
                fontSize: 30,
                animation: "tiqSpark 2s ease-in-out infinite",
                "@keyframes tiqSpark": {
                  "0%, 100%": { opacity: 1, transform: "rotate(0deg)" },
                  "50%": { opacity: 0.65, transform: "rotate(12deg)" },
                },
              }}
            />
          )}
        </Box>
      </Box>

      <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#0f172a" }}>
        {title}
      </Typography>
      {message ? (
        <Typography sx={{ fontSize: "14px", color: "#64748b", minHeight: "20px", lineHeight: 1.6 }}>
          {message}
        </Typography>
      ) : null}

      {/* Bouncing dots */}
      <Box sx={{ display: "flex", gap: 0.75, mt: 0.5 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#1c97e3",
              animation: "tiqBounce 1.2s ease-in-out infinite",
              animationDelay: `${i * 0.15}s`,
              "@keyframes tiqBounce": {
                "0%, 100%": { opacity: 0.3, transform: "translateY(0)" },
                "50%": { opacity: 1, transform: "translateY(-6px)" },
              },
            }}
          />
        ))}
      </Box>

      {/* Persistent "please wait, don't refresh" notice */}
      {note ? (
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            px: 2,
            py: 1.25,
            borderRadius: "12px",
            backgroundColor: "#fff8e1",
            border: "1px solid #ffe082",
            textAlign: "left",
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "#b45309", mt: "1px" }} />
          <Typography sx={{ fontSize: "12.5px", color: "#92400e", fontWeight: 600, lineHeight: 1.55 }}>
            {note}
          </Typography>
        </Box>
      ) : null}
    </Box>
  </Backdrop>
  );
};

export default LoadingOverlay;
